import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  UserGroupIcon, 
  PlusIcon, 
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { patientsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmDialog from '../components/ConfirmDialog';

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    patientId: null,
    patientName: '',
  });

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const data = await patientsService.getAll();
      setPatients(data);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar pacientes:', err);
      setError('Erro ao carregar pacientes. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id, name) => {
    setConfirmDialog({
      isOpen: true,
      patientId: id,
      patientName: name,
    });
  };

  const handleDeleteConfirm = async () => {
    const { patientId } = confirmDialog;
    
    // Toast de loading
    const loadingToast = toast.loading('Excluindo paciente e dados relacionados...');

    try {
      await patientsService.delete(patientId);
      await loadPatients();
      
      // Dismiss loading e mostrar sucesso
      toast.dismiss(loadingToast);
      toast.success('Paciente excluído com sucesso!', {
        icon: '🗑️',
        duration: 3000,
      });
    } catch (err) {
      console.error('Erro ao excluir paciente:', err);
      
      // Dismiss loading e mostrar erro
      toast.dismiss(loadingToast);
      toast.error('Erro ao excluir paciente. Tente novamente.', {
        duration: 5000,
      });
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialog({
      isOpen: false,
      patientId: null,
      patientName: '',
    });
  };

  const filteredPatients = patients.filter((patient) =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Pacientes</h1>
            <p className="text-gray-600">Gerencie seus pacientes cadastrados</p>
          </div>
          <Link to="/patients/new" className="btn-primary flex items-center space-x-2">
            <PlusIcon className="w-5 h-5" />
            <span>Novo Paciente</span>
          </Link>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar paciente por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando pacientes...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
          <button onClick={loadPatients} className="text-red-600 hover:text-red-800 font-semibold mt-2">
            Tentar novamente
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredPatients.length === 0 && (
        <div className="card text-center py-12">
          <UserGroupIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            {searchTerm ? 'Nenhum paciente encontrado' : 'Nenhum paciente cadastrado'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm 
              ? 'Tente buscar por outro nome' 
              : 'Comece cadastrando seu primeiro paciente'}
          </p>
          {!searchTerm && (
            <Link to="/patients/new" className="btn-primary inline-block">
              Cadastrar Primeiro Paciente
            </Link>
          )}
        </div>
      )}

      {/* Patients Table */}
      {!loading && !error && filteredPatients.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Idade
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sexo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cadastrado em
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-medical-100 rounded-full flex items-center justify-center">
                          <span className="text-medical-600 font-semibold">
                            {patient.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{patient.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{patient.age || calculateAge(patient.birthDate)} anos</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {patient.sex === 'M' ? 'Masculino' : 'Feminino'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(patient.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/patients/${patient.id}`}
                          className="text-medical-600 hover:text-medical-900 p-2 hover:bg-medical-50 rounded-lg transition-colors"
                          title="Visualizar"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </Link>
                        <Link
                          to={`/patients/${patient.id}/evolution`}
                          className="text-purple-600 hover:text-purple-900 p-2 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Ver Evolução"
                        >
                          <ChartBarIcon className="w-5 h-5" />
                        </Link>
                        <Link
                          to={`/patients/${patient.id}/edit`}
                          className="text-health-600 hover:text-health-900 p-2 hover:bg-health-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(patient.id, patient.name)}
                          className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stats Footer */}
      {!loading && !error && patients.length > 0 && (
        <div className="mt-6 text-sm text-gray-600">
          Mostrando <span className="font-semibold">{filteredPatients.length}</span> de{' '}
          <span className="font-semibold">{patients.length}</span> pacientes
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={handleCloseDialog}
        onConfirm={handleDeleteConfirm}
        title="⚠️ Exclusão Permanente"
        message={`Você está prestes a excluir o paciente "${confirmDialog.patientName}" e TODOS os dados associados:`}
        details={[
          'Todos os exames audiométricos',
          'Todos os relatórios gerados',
          'Todos os arquivos e imagens',
          'Todo o histórico e evolução',
        ]}
        confirmText="Sim, excluir permanentemente"
        cancelText="Cancelar"
        type="danger"
      />
    </DashboardLayout>
  );
};

export default Patients;
